import type { Ring } from '../../../../geo/types';
import { octahedronSolid, planPoint, pyramidOverRing, ringPoints } from '../../solids';
import { pointedArchRise, pointedWindow } from '../../panels';
import { rectWall } from '../masonry';
import { pinnacleSolid } from '../gothic/pinnacle';
import type { NieuweKerkBuild } from './nieuweKerkParts';

/**
 * The flèche over the crossing as the 1544 woodcut draws it (uva 2120-2260 x 1690-1800): a lead-clad
 * octagon rising from the crossing of the ridges, open in every face at its lantern, a crown of
 * pinnacles round the foot of its needle, and a finial. The painting draws it dark, as the darkest
 * slate (#494b26, scan 3776-3786 x 1740-1800); its sizes are not recorded.
 */
export const FLECHE = {
  segments: 8,
  opening: { widthShare: 0.5, sill: 0.35, topDrop: 0.4 },
  pinnacle: { half: 0.12, below: 0.3 },
  finialRise: 0.15,
  finial: 0.3,
} as const;

/* Flat faces toward the four arms, over the crossing square. */
const PHASE = Math.PI / FLECHE.segments;

function opening(face: number, f: NieuweKerkBuild['plan']['fleche']): Ring {
  const o = FLECHE.opening;
  const width = o.widthShare * face;
  const sill = f.lanternFoot - f.footY + o.sill;
  const top = f.lanternTop - f.footY - o.topDrop;
  return pointedWindow(face / 2, sill, width, top - pointedArchRise(width), 3);
}

export function buildFleche(s: NieuweKerkBuild): void {
  const f = s.plan.fleche;
  const ring = ringPoints(f.center, f.radius, FLECHE.segments, PHASE);
  const face = Math.hypot(ring[1]![0] - ring[0]![0], ring[1]![1] - ring[0]![1]);
  const lantern = { wall: s.lead, roof: s.lead, opening: s.stone.opening };
  ring.forEach((p, i) => { rectWall(s.builder, lantern, s.frame, [p, ring[(i + 1) % ring.length]!], [f.footY, f.lanternTop], [opening(face, f)], f.footY); });
  pyramidOverRing(s.builder, { roof: s.stone.roof, wall: s.stone.roof }, s.frame, ring, f.lanternTop, { plan: f.center, y: f.tipY });
  const p = FLECHE.pinnacle;
  for (const corner of ring) {
    pinnacleSolid(s.builder, s.lead, s.frame, { center: corner, half: p.half, y0: f.lanternTop - p.below, shaftTop: f.lanternTop, tipY: f.crownTop });
  }
  octahedronSolid(s.builder, s.lead, s.frame, planPoint(f.center[0], f.tipY + FLECHE.finialRise, f.center[1]), FLECHE.finial);
}
